import React, { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight, Clock } from 'lucide-react';
import { useInViewAnimation } from '../hooks/useInViewAnimation';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function estimateReadTime(body) {
  const words = (body || '').split(/\s+/).length;
  return Math.max(1, Math.ceil(words / 250));
}

// Simple markdown renderer for blog body
function renderMarkdown(text) {
  if (!text) return '';
  const lines = text.split('\n');
  const html = [];
  let inList = false;

  for (const line of lines) {
    // Headings
    if (line.startsWith('## ')) {
      if (inList) { html.push('</ul>'); inList = false; }
      html.push(`<h2 class="text-2xl font-semibold text-[#051A24] mt-10 mb-4">${escapeAndFormat(line.slice(3))}</h2>`);
    } else if (line.startsWith('### ')) {
      if (inList) { html.push('</ul>'); inList = false; }
      html.push(`<h3 class="text-xl font-medium text-[#051A24] mt-8 mb-3">${escapeAndFormat(line.slice(4))}</h3>`);
    } else if (line.startsWith('- ')) {
      if (!inList) { html.push('<ul class="list-disc pl-6 space-y-2 mb-6 text-[#273C46]">'); inList = true; }
      html.push(`<li>${escapeAndFormat(line.slice(2))}</li>`);
    } else if (line.trim() === '') {
      if (inList) { html.push('</ul>'); inList = false; }
    } else {
      if (inList) { html.push('</ul>'); inList = false; }
      html.push(`<p class="text-[#273C46] leading-relaxed mb-4">${escapeAndFormat(line)}</p>`);
    }
  }
  if (inList) html.push('</ul>');
  return html.join('\n');
}

function escapeAndFormat(text) {
  return text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong class="text-[#051A24] font-semibold">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-[#051A24] underline hover:text-[#273C46] transition-colors">$1</a>')
    .replace(/—/g, '\u2014');
}

const CategoryBadge = ({ category }) => {
  const colors = {
    'AI & Product': 'bg-blue-50 text-blue-700',
    'Web3': 'bg-purple-50 text-purple-700',
    'Business': 'bg-green-50 text-green-700',
    'Engineering': 'bg-orange-50 text-orange-700',
    'Startups': 'bg-teal-50 text-teal-700',
  };
  return (
    <span className={`text-xs font-medium px-3 py-1 rounded-full ${colors[category] || 'bg-[#E0EBF0] text-[#273C46]'}`}>
      {category}
    </span>
  );
};

const BlogCard = ({ post, onClick }) => {
  const { ref, isInView } = useInViewAnimation();

  return (
    <article
      ref={ref}
      onClick={onClick}
      className={`group cursor-pointer bg-white border border-[#E0EBF0] rounded-2xl overflow-hidden hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] transition-all duration-300 ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
      data-testid={`blog-card-${post.slug}`}
    >
      {post.coverImage && (
        <div className="aspect-[16/9] overflow-hidden">
          <img
            src={post.coverImage}
            alt={post.coverAlt || post.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      )}
      <div className="p-6">
        <div className="flex items-center gap-3 mb-3">
          {post.category && <CategoryBadge category={post.category} />}
          <span className="flex items-center gap-1 text-xs text-[#273C46]/60">
            <Clock className="w-3 h-3" />
            {estimateReadTime(post.body)} min read
          </span>
        </div>
        <h3 className="text-lg font-semibold text-[#051A24] mb-2 group-hover:text-[#273C46] transition-colors leading-snug">
          {post.title}
        </h3>
        <p className="text-sm text-[#273C46] leading-relaxed line-clamp-3 mb-4">
          {post.excerpt}
        </p>
        <div className="flex items-center justify-between">
          <span className="text-xs text-[#273C46]/60">{formatDate(post.publishedAt)}</span>
          <span className="text-sm font-medium text-[#051A24] flex items-center gap-1 group-hover:gap-2 transition-all">
            Read <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </article>
  );
};

const BlogPost = ({ post, onBack }) => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="blog-post-view">
      {/* Header */}
      <div className="max-w-3xl mx-auto px-6 pt-8">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-[#273C46] hover:text-[#051A24] transition-colors mb-8"
          data-testid="blog-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to blog
        </button>
      </div>

      {/* Cover Image */}
      {post.coverImage && (
        <div className="max-w-4xl mx-auto px-6 mb-8">
          <img
            src={post.coverImage}
            alt={post.coverAlt || post.title}
            className="w-full aspect-[2/1] object-cover rounded-2xl"
          />
        </div>
      )}

      {/* Article */}
      <article className="max-w-3xl mx-auto px-6 pb-20">
        <div className="flex items-center gap-3 mb-4">
          {post.category && <CategoryBadge category={post.category} />}
          <span className="flex items-center gap-1 text-sm text-[#273C46]/60">
            <Clock className="w-3.5 h-3.5" />
            {estimateReadTime(post.body)} min read
          </span>
        </div>
        <h1
          className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#051A24] leading-tight mb-4"
          data-testid="blog-post-title"
        >
          {post.title}
        </h1>
        <div className="flex items-center gap-3 mb-10 text-sm text-[#273C46]/60">
          <span>By {post.author}</span>
          <span>&middot;</span>
          <span>{formatDate(post.publishedAt)}</span>
        </div>
        <div
          className="prose-custom"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(post.body) }}
        />
      </article>
    </div>
  );
};

export const BlogPage = ({ onNavigateHome, initialSlug, onSlugChange }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const { ref, isInView } = useInViewAnimation();

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch('/api/blog')
      .then((r) => r.json())
      .then((data) => {
        const fetched = data.posts || [];
        setPosts(fetched);
        // If URL has a slug, open that post directly
        if (initialSlug && fetched.length > 0) {
          const cleanSlug = initialSlug.toLowerCase().replace(/\/+$/, '');
          const match = fetched.find((p) => {
            const postSlug = (p.slug || '').toLowerCase();
            return postSlug === cleanSlug || postSlug.startsWith(cleanSlug) || cleanSlug.startsWith(postSlug);
          });
          if (match) {
            setSelectedPost(match);
            document.title = `${match.title} — Brandon Crenshaw`;
          }
        } else {
          document.title = 'Writing — Brandon Crenshaw';
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [initialSlug]);

  const selectPost = (post) => {
    setSelectedPost(post);
    document.title = `${post.title} — Brandon Crenshaw`;
    if (onSlugChange) onSlugChange(post.slug);
  };

  const deselectPost = () => {
    setSelectedPost(null);
    document.title = 'Writing — Brandon Crenshaw';
    if (onSlugChange) onSlugChange(null);
  };

  if (selectedPost) {
    return <BlogPost post={selectedPost} onBack={deselectPost} />;
  }

  return (
    <div className="min-h-screen bg-white" data-testid="blog-page">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 pt-8">
        <button
          onClick={onNavigateHome}
          className="flex items-center gap-2 text-sm text-[#273C46] hover:text-[#051A24] transition-colors mb-12"
          data-testid="blog-home-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Home
        </button>
      </div>

      {/* Hero */}
      <div ref={ref} className="max-w-7xl mx-auto px-6 mb-16">
        <h1
          className={`text-4xl md:text-5xl font-semibold text-[#051A24] mb-4 ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
        >
          The{' '}
          <span style={{ fontFamily: "'Playfair Display', serif" }} className="italic">
            Blog
          </span>
        </h1>
        <p
          className={`text-[#273C46] text-lg max-w-xl ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
          style={{ animationDelay: '0.1s' }}
        >
          Thoughts on building products, AI, business development, and the future of tech.
        </p>
      </div>

      {/* Posts Grid */}
      <div className="max-w-7xl mx-auto px-6 pb-20">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-[#E0EBF0]/30 rounded-2xl h-96 animate-pulse" />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <p className="text-[#273C46] text-center py-20">No posts yet. Check back soon.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post) => (
              <BlogCard key={post.id} post={post} onClick={() => selectPost(post)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
